import React, { useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { DutyRow, DutySections } from "../Generated/avroGraphqlDuty";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AccordionIndexEnum } from "./PilotDutyPage";
import { getBackgroundColorForLimit } from "../Utils/UtilityMethods";

interface Props {
    expandedAccordions: number[];
    accordionClicked(arg0: number): void;
    error: any;
    data: any;
    loading: boolean;
}

interface State {
    tooltip_duty_row_id: number;
}

export default function DutyHoursSummarySection(props: Props): JSX.Element {
    const [localState, setLocalState] = useState<State>({ tooltip_duty_row_id: -1 });

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isMedium = useMediaQuery(theme.breakpoints.up("sm"));

    let horizontalPadding = 4;

    if (isMedium) horizontalPadding = 6;

    const handleTooltipClose = (e: Event | React.SyntheticEvent<Element, Event>): void => {
        e.preventDefault();

        setLocalState({ ...localState, tooltip_duty_row_id: -1 });
    };

    const handleTooltipOpen = (e: Event | React.SyntheticEvent<Element, Event>, duty_row_id: number): void => {
        e.preventDefault();

        // tooltip shows up on click and then if the same icon is click again then the tooltip vanishes
        let new_duty_row_id = -1;
        if (duty_row_id !== localState.tooltip_duty_row_id) new_duty_row_id = duty_row_id;

        setLocalState({ ...localState, tooltip_duty_row_id: new_duty_row_id });
    };

    return (
        <>
            {props.loading ? (
                <Grid container mt={4} justifyContent="center">
                    <CircularProgress />
                </Grid>
            ) : (
                <FormControl fullWidth sx={{ px: horizontalPadding }}>
                    {!props.loading &&
                        props.data &&
                        props.data.getDutyTimesSummary &&
                        props.data.getDutyTimesSummary.sections &&
                        props.data.getDutyTimesSummary.sections.map((section_element: DutySections, index: number) => {
                            return (
                                <Accordion
                                    key={index}
                                    sx={{ background: "#F7F8F9", mt: 4 }}
                                    expanded={
                                        section_element && section_element.section && section_element.section.node_id
                                            ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                              // @ts-ignore
                                              props.expandedAccordions.includes(AccordionIndexEnum[`${section_element.section.node_id}`])
                                            : false
                                    }
                                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                    // @ts-ignore
                                    onChange={() => props.accordionClicked(AccordionIndexEnum[`${section_element.section.node_id}`])}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography marginLeft={2} fontSize={18} variant="button">
                                            {section_element?.section?.label}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {section_element?.section?.rows && section_element.section.rows.length === 0
                                            ? null
                                            : section_element?.section &&
                                              section_element?.section.rows &&
                                              section_element.section.rows.map((duty_row, duty_row_index) => {
                                                  const isValueText = duty_row?.label ? true : false;
                                                  const backgroundColor = getBackgroundColorForLimit(duty_row as DutyRow);

                                                  return (
                                                      <Box key={duty_row?.id} sx={{ marginLeft: 2, marginTop: 2 }}>
                                                          <Grid
                                                              container
                                                              alignItems="baseline"
                                                              marginLeft={
                                                                  duty_row?.indent_level && duty_row?.indent_level === 1
                                                                      ? 0
                                                                      : (duty_row?.indent_level ?? 0) * (isMobile ? 1.5 : 3)
                                                              }>
                                                              {isValueText ? (
                                                                  <Grid
                                                                      item
                                                                      xs={isMobile ? 12 : 6}
                                                                      display="flex"
                                                                      alignItems={"center"}
                                                                      sx={{ paddingLeft: 0 }}>
                                                                      <TextField
                                                                          size="small"
                                                                          sx={{ width: 90, minWidth: 90 }}
                                                                          inputProps={{
                                                                              style: {
                                                                                  textAlign: "end",
                                                                                  backgroundColor: backgroundColor,
                                                                                  color: backgroundColor === "white" ? "black" : "white",
                                                                                  borderRadius: 4,
                                                                              },
                                                                          }}
                                                                          value={duty_row?.value?.value.toFixed(1)}
                                                                      />
                                                                      <Typography fontSize={16} sx={{ marginLeft: 1, display: "inline" }}>
                                                                          {duty_row?.label}
                                                                      </Typography>

                                                                      {isMobile && duty_row?.requirement ? (
                                                                          <Tooltip
                                                                              title={duty_row?.requirement}
                                                                              PopperProps={{
                                                                                  disablePortal: true,
                                                                              }}
                                                                              onClose={(e) => handleTooltipClose(e)}
                                                                              open={duty_row.id === localState.tooltip_duty_row_id}
                                                                              disableFocusListener
                                                                              disableHoverListener
                                                                              disableTouchListener>
                                                                              <IconButton onClick={(e) => handleTooltipOpen(e, duty_row.id)}>
                                                                                  <InfoIcon color="info" />
                                                                              </IconButton>
                                                                          </Tooltip>
                                                                      ) : null}
                                                                  </Grid>
                                                              ) : null}
                                                              {!isMobile ? (
                                                                  <Grid item xs={isValueText ? 6 : 12}>
                                                                      <Typography fontSize={16} sx={{ marginLeft: 1 }}>
                                                                          {duty_row?.requirement && duty_row?.requirement !== "null"
                                                                              ? duty_row?.requirement
                                                                              : ""}
                                                                      </Typography>
                                                                  </Grid>
                                                              ) : null}
                                                          </Grid>
                                                      </Box>
                                                  );
                                              })}
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })}
                </FormControl>
            )}
        </>
    );
}
